// The filter card of 04 §2, and the presets behind it.
// Fast client-side filtering and intuitive preset chips.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from '../../lib/supabase.js';
import type { LeadListRow } from './useLeads.js';
import { IconClose, IconFilter, IconSearch } from '../../components/Icons.js';
import { NicheFilterFields, NO_NICHE_OPTIONS, type NicheOptions } from './NicheFilterFields.js';

import { useStages } from '../sales/useSales.js';

export interface LeadFilterState {
  search: string;
  tier: string;
  priority: string;
  hasWebsite: 'any' | 'yes' | 'no';
  minScore: number;
  stageId: string;
  hasEik: 'any' | 'yes' | 'no';
  minRating: number;
  includeDnc: boolean;
  sort: 'score' | 'name' | 'newest';
  /** Niche context (plan §4.5): '' = all. */
  vertical: string;
  city: string;
}

export const EMPTY_FILTERS: LeadFilterState = {
  search: '',
  tier: '',
  priority: '',
  hasWebsite: 'any',
  minScore: 0,
  stageId: '',
  hasEik: 'any',
  minRating: 0,
  includeDnc: false,
  sort: 'score',
  vertical: '',
  city: '',
};

function isFilteredState(f: LeadFilterState): boolean {
  return (
    f.tier !== '' ||
    f.priority !== '' ||
    f.hasWebsite !== 'any' ||
    f.minScore > 0 ||
    f.stageId !== '' ||
    f.hasEik !== 'any' ||
    f.minRating > 0 ||
    f.includeDnc ||
    f.vertical !== '' ||
    f.city !== ''
  );
}

function matchesScopeAndDncFilters(lead: LeadListRow, filters: LeadFilterState): boolean {
  if (filters.vertical !== '' && lead.vertical_key !== filters.vertical) return false;
  if (filters.city !== '' && lead.city_key !== filters.city) return false;
  if (!filters.includeDnc && lead.do_not_contact) return false;
  if (filters.tier !== '' && lead.tier !== filters.tier) return false;
  if (filters.priority !== '' && lead.priority !== filters.priority) return false;
  return true;
}

function matchesNumericAndSiteFilters(lead: LeadListRow, filters: LeadFilterState): boolean {
  if (filters.minScore > 0 && (lead.lead_score ?? 0) < filters.minScore) return false;
  if (filters.minRating > 0 && (lead.rating ?? 0) < filters.minRating) return false;
  if (filters.hasWebsite === 'yes' && !lead.website_url) return false;
  if (filters.hasWebsite === 'no' && lead.website_url) return false;
  return true;
}

function matchesStageAndEikFilters(lead: LeadListRow, filters: LeadFilterState): boolean {
  const stageStr =
    lead.sales_stage_id === null || lead.sales_stage_id === undefined
      ? 'unstaged'
      : String(lead.sales_stage_id);
  if (filters.stageId !== '' && stageStr !== filters.stageId) return false;
  const hasConfirmedEik = Boolean(
    lead.legal_entities?.eik && (lead.legal_entities.match_confidence ?? 0) >= 80,
  );
  if (filters.hasEik === 'yes' && !hasConfirmedEik) return false;
  if (filters.hasEik === 'no' && hasConfirmedEik) return false;
  return true;
}

export function useLeadFilters() {
  const [filters, setFilters] = useState<LeadFilterState>(EMPTY_FILTERS);

  const apply = (rows: readonly LeadListRow[]): LeadListRow[] => {
    const matched = rows.filter(
      (lead) =>
        matchesScopeAndDncFilters(lead, filters) &&
        matchesNumericAndSiteFilters(lead, filters) &&
        matchesStageAndEikFilters(lead, filters),
    );

    const sorted = [...matched];
    if (filters.sort === 'name') {
      sorted.sort((a, b) => a.business_name.localeCompare(b.business_name, 'bg'));
    } else if (filters.sort === 'newest') {
      sorted.sort((a, b) => b.id - a.id);
    } else {
      sorted.sort((a, b) => (b.lead_score ?? -1) - (a.lead_score ?? -1));
    }
    return sorted;
  };

  return { filters, setFilters, apply };
}

interface PresetRow {
  id: number;
  name: string;
  filter_data: LeadFilterState;
}

const DEMO_PRESETS: PresetRow[] = [
  { id: 1, name: 'Топ Tier A', filter_data: { ...EMPTY_FILTERS, tier: 'A', minScore: 80 } },
  { id: 2, name: 'Без сайт', filter_data: { ...EMPTY_FILTERS, hasWebsite: 'no' } },
  { id: 3, name: 'Висок приоритет', filter_data: { ...EMPTY_FILTERS, priority: 'High' } },
];

function usePresets() {
  return useQuery({
    queryKey: ['saved_filters', 'leads'],
    queryFn: async (): Promise<PresetRow[]> => {
      if (!isConfigured || isDemoMode) return DEMO_PRESETS;
      const { data, error } = await supabase
        .from('saved_filters')
        .select('id, name, filter_data')
        .eq('kind', 'leads')
        .order('name');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PresetRow[];
    },
  });
}

function useSavePreset() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, filters }: { name: string; filters: LeadFilterState }) => {
      if (!isConfigured || isDemoMode) {
        DEMO_PRESETS.push({ id: Date.now(), name, filter_data: filters });
        return;
      }
      const { error } = await supabase
        .from('saved_filters')
        .upsert({ kind: 'leads', name, filter_data: filters }, { onConflict: 'kind,name' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ['saved_filters', 'leads'] }),
  });
}

export function LeadFilters({
  filters,
  onChange,
  count,
  niche = NO_NICHE_OPTIONS,
}: {
  filters: LeadFilterState;
  onChange: (next: LeadFilterState) => void;
  count: number;
  /** Vertical/city options present in the list; the selects hide when there is only one. */
  niche?: NicheOptions;
}) {
  const [open, setOpen] = useState(false);
  const { data: presets = [] } = usePresets();
  const { data: stages = [] } = useStages();
  const savePreset = useSavePreset();

  const set = <K extends keyof LeadFilterState>(key: K, value: LeadFilterState[K]): void =>
    onChange({ ...filters, [key]: value });

  const isFiltered = isFilteredState(filters);

  return (
    <div className="lead-filters">
      {/* Primary Search & Count Bar */}
      <div className="lead-filters__searchbar">
        <div className="lead-filters__inputwrap">
          <IconSearch size={15} className="lead-filters__searchicon" />
          <input
            type="search"
            placeholder="Търсене по име, телефон или ЕИК…"
            value={filters.search}
            onChange={(e) => set('search', e.target.value)}
            className="lead-filters__searchinput"
          />
          {filters.search && (
            <button
              type="button"
              className="lead-filters__clearbtn"
              onClick={() => set('search', '')}
            >
              <IconClose size={13} />
            </button>
          )}
        </div>
        <span className="badge badge--score" title="Намерени обекти">
          {count}
        </span>
      </div>

      {/* Quick Filter Preset Chips */}
      <div className="lead-filters__chips">
        <button
          type="button"
          className={`lead-filters__chip${!isFiltered ? ' lead-filters__chip--active' : ''}`}
          onClick={() => onChange({ ...EMPTY_FILTERS, search: filters.search })}
        >
          Всички
        </button>
        <button
          type="button"
          className={`lead-filters__chip${filters.tier === 'A' ? ' lead-filters__chip--active' : ''}`}
          onClick={() => set('tier', filters.tier === 'A' ? '' : 'A')}
        >
          Tier A
        </button>
        <button
          type="button"
          className={`lead-filters__chip${filters.hasWebsite === 'no' ? ' lead-filters__chip--active' : ''}`}
          onClick={() => set('hasWebsite', filters.hasWebsite === 'no' ? 'any' : 'no')}
        >
          Без сайт
        </button>
        <button
          type="button"
          className={`lead-filters__chip${filters.priority === 'High' ? ' lead-filters__chip--active' : ''}`}
          onClick={() => set('priority', filters.priority === 'High' ? '' : 'High')}
        >
          Висок приоритет
        </button>

        <NicheFilterFields
          options={niche}
          filters={filters}
          onChange={(patch) => onChange({ ...filters, ...patch })}
        />

        <button
          type="button"
          className={`lead-filters__togglebtn${open ? ' lead-filters__togglebtn--open' : ''}${isFiltered ? ' lead-filters__togglebtn--filtered' : ''}`}
          onClick={() => setOpen(!open)}
          title="Разширени филтри"
        >
          <IconFilter size={13} />
          <span>Филтри</span>
        </button>
      </div>

      {/* Advanced Filter Drawer */}
      {open && (
        <div className="lead-filters__advanced">
          <div className="lead-filters__advhead">
            <span className="lead-filters__advtitle">Разширени филтри</span>
            <button
              type="button"
              className="lead-filters__resetbtn"
              onClick={() => onChange({ ...EMPTY_FILTERS, search: filters.search })}
            >
              Изчисти
            </button>
          </div>

          <div className="lead-filters__advgrid">
            <div className="field">
              <label className="field__label">Ниво (Tier)</label>
              <select
                className="select"
                value={filters.tier}
                onChange={(e) => set('tier', e.target.value)}
              >
                <option value="">Всички нива</option>
                <option value="A">Tier A (Най-висок потенциал)</option>
                <option value="B">Tier B (Добър потенциал)</option>
                <option value="C">Tier C (Среден потенциал)</option>
                <option value="D">Tier D (Нисък потенциал)</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label">Приоритет</label>
              <select
                className="select"
                value={filters.priority}
                onChange={(e) => set('priority', e.target.value)}
              >
                <option value="">Всички приоритети</option>
                <option value="High">Висок</option>
                <option value="Medium">Среден</option>
                <option value="Low">Нисък</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label">Наличие на уебсайт</label>
              <select
                className="select"
                value={filters.hasWebsite}
                onChange={(e) => set('hasWebsite', e.target.value as LeadFilterState['hasWebsite'])}
              >
                <option value="any">Без значение</option>
                <option value="no">Само без сайт</option>
                <option value="yes">Само със сайт</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label">Търговски етап</label>
              <select
                className="select"
                value={filters.stageId}
                onChange={(e) => set('stageId', e.target.value)}
              >
                <option value="">Всички етапи</option>
                <option value="unstaged">Без етап</option>
                {stages.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="field__label">ЕИК в регистъра</label>
              <select
                className="select"
                value={filters.hasEik}
                onChange={(e) => set('hasEik', e.target.value as LeadFilterState['hasEik'])}
              >
                <option value="any">Без значение</option>
                <option value="yes">С потвърден ЕИК (≥80%)</option>
                <option value="no">Без потвърден ЕИК</option>
              </select>
            </div>

            <div className="field">
              <label className="field__label">Мин. Google рейтинг</label>
              <select
                className="select"
                value={String(filters.minRating)}
                onChange={(e) => set('minRating', Number(e.target.value))}
              >
                <option value="0">Без значение</option>
                <option value="3.5">≥ 3.5 ★</option>
                <option value="4">≥ 4.0 ★</option>
                <option value="4.5">≥ 4.5 ★</option>
                <option value="4.8">≥ 4.8 ★</option>
              </select>
            </div>

            <div className="field">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <label className="field__label">Минимум точки</label>
                <span className="badge badge--score">{filters.minScore} т.</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={filters.minScore}
                onChange={(e) => set('minScore', Number(e.target.value))}
                style={{ accentColor: 'var(--accent)', marginTop: '4px' }}
              />
            </div>
          </div>

          <label className="lead-filters__checkboxlabel">
            <input
              type="checkbox"
              checked={filters.includeDnc}
              onChange={(e) => set('includeDnc', e.target.checked)}
            />
            <span>Показвай и маркирани „Не безпокой“ (DNC)</span>
          </label>

          <div className="lead-filters__presetsrow">
            <select
              className="select"
              value=""
              onChange={(e) => {
                const preset = presets.find((p) => String(p.id) === e.target.value);
                if (preset) onChange({ ...EMPTY_FILTERS, ...preset.filter_data });
              }}
            >
              <option value="">Запазени филтри…</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => {
                const name = window.prompt('Име на филтъра?');
                if (name) savePreset.mutate({ name, filters });
              }}
            >
              Запази текущия
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
